<script>
import moment from "moment-timezone";
import i18n from "@/i18n";
import {mapActions, mapState} from "pinia";
import {email, helpers, minLength, required} from "@vuelidate/validators";
import {authStore} from "@/store/authStore";
import {useVuelidate} from "@vuelidate/core";
import {baseLoaderStore} from "@/store/baseLoaderStore";
import AppBaseView from "@/mobile/AppBaseView.vue";
import Select from 'primevue/select';
import DatePicker from 'primevue/datepicker';

export default {
  name: "SignUpView",
  setup () {
    return { v$: useVuelidate() }
  },
  components: {
    AppBaseView,
    Select,
    DatePicker
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      gender: null,
      birthday: null,
      submitBtnState: true,
      firstNameActive: false,
      lastNameActive: false,
      emailActive: false,
      passwordActive: false,
    }
  },
  validations () {
    return {
      firstName: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required)
      },
      lastName: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required)
      },
      email: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required),
        email: helpers.withMessage(i18n.global.t('validations.email'), email)
      },
      password: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required),
        minLength: minLength(6),
        notSimplestThan: helpers.withMessage(i18n.global.t('validations.simplePassword'),  val => /^(?=.*[0-9])(?=.*[a-zA-Z])(.+)$/.test(val)),
      },
      birthday: {
        required: helpers.withMessage(i18n.global.t('validations.enterBirthday'), required)
      },

    }
  },
  computed: {
    ...mapState(authStore, [
      'signUpModalState',
    ]),
    modalState: {
      get() {
        return this.signUpModalState;
      },
      set() {
        this.closeModal();
      },
    },
    genderSelectOptions() {
      return [
        { name: i18n.global.t('auth.signUpForm.gender'), code: 'NONE' },
        { name: i18n.global.t('auth.signUpForm.male'), code: 'MALE' },
        { name: i18n.global.t('auth.signUpForm.female'), code: 'FEMALE' }
      ]
    },
    userAgeCheck() {
      const birthday = moment(this.birthday, 'YYYY-MM-DD', true);
      const age = moment().diff(birthday, 'years');

      return this.birthday ? (age >= 13) : true;
    },
    hasChanges() {
      return this.firstName.length > 0
          || this.lastName.length > 0
          || this.email.length > 0
          || this.password.length > 0
          || this.gender.code !== 'NONE'
          || this.birthday !== null;
    }
  },
  methods: {
    ...mapActions(authStore, [
      'initSignUpView',
      'setSignUpErrorCallback',
      'signUpRequest',
      'toggleEmailExistModalState',
      'authGetUserData'
    ]),
    ...mapActions(baseLoaderStore, [
      'showBaseLoader',
      'hideBaseLoader'
    ]),
    openUserTerms() {
      let route = this.$router.resolve({ path: "/userterms" });
      window.open(route.href);
    },
    async signUp() {
      const isFormCorrect = await this.v$.$validate()
      const formData = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        password: this.password,
        gender: this.gender.code,
        birthday: moment(this.birthday).format('YYYY-MM-DD')
      };

      if (this.submitBtnState) {
        this.v$.$touch();
        if (isFormCorrect && this.userAgeCheck) {
          this.showBaseLoader();
          this.submitBtnState = false;
          this.setSignUpErrorCallback((error)=> {
            if (error.response) {
              const errorType = error.response.data;
              if (errorType.email) {
                if (errorType.email === 'emailExists') {
                  this.toggleEmailExistModalState();
                  this.submitBtnState = true;
                }
              }
            }
          })
          this.signUpRequest(formData).then(()=> {
            this.hideBaseLoader();
          });
        }
      }
    }
  },
  mounted() {
    setTimeout(()=> {
      this.gender = this.genderSelectOptions[0];
    }, 0);

    this.initSignUpView(()=> {
      this.authGetUserData();
      this.$router.go(-1);
    })
  }
}
</script>

<template>
<app-base-view :show-header="false">
  <div class="sign-up-view">
    <div class="wizard-view-header">
      <a href="/" class="wizard-logo-icon"></a>
    </div>

    <form autocomplete="off" class="sign-up-form">
      <h3 class="sign-up-view__title">{{$t('auth.signUpForm.creatingAccount')}}</h3>
      <div class="field-wrap">
        <label class="form-label">{{ $t('auth.signUpForm.firstname') }}</label>
        <input type="text"
               v-model="firstName"
               :placeholder="$t('auth.signUpForm.firstname')"
               class="form-control"
               :class="{'form-control--error': v$.firstName.$error}"
               @focus="firstNameActive = true"
               @blur="firstNameActive = false"
               maxlength="30"
               autocomplete="one-time-code"
               autofocus
        />
        <span v-for="error of v$.firstName.$errors" :key="error.$uid"
              class="form-control-error"
        >
          <template v-if="!firstNameActive">{{ error.$message }}</template>
        </span>
        <span v-if="firstNameActive" class="form-control__char-count">
          {{30 - firstName.length}}
        </span>
      </div>
      <div class="field-wrap">
        <label class="form-label">{{ $t('auth.signUpForm.lastName') }}</label>
        <input type="text"
               v-model="lastName"
               :placeholder="$t('auth.signUpForm.lastName')"
               class="form-control"
               :class="{'form-control--error': v$.lastName.$error}"
               @focus="lastNameActive = true"
               @blur="lastNameActive = false"
               maxlength="30"
               autocomplete="one-time-code"
               autofocus
        />
        <span v-for="error of v$.lastName.$errors" :key="error.$uid"
              class="form-control-error"
        >
          <template v-if="!lastNameActive">
            {{ error.$message }}
          </template>
        </span>
        <span v-if="lastNameActive" class="form-control__char-count">
          {{30 - lastName.length}}
        </span>
      </div>
      <div class="field-wrap">
        <label class="form-label">{{ $t('auth.signUpForm.email') }}</label>
        <input type="text"
               v-model="email"
               :placeholder="$t('auth.signUpForm.email')"
               class="form-control"
               :class="{'form-control--error': v$.email.$error}"
               @focus="emailActive = true"
               @blur="emailActive = false"
               maxlength="60"
               autocomplete="one-time-code"
        />
        <span v-for="error of v$.email.$errors" :key="error.$uid"
              class="form-control-error"
        >
          <template v-if="!emailActive">
            {{ error.$message }}
          </template>
        </span>
        <span v-if="emailActive" class="form-control__char-count">
          {{60 - email.length}}
        </span>
      </div>
      <div class="field-wrap">
        <label class="form-label">{{ $t('auth.signUpForm.password') }}</label>
        <input type="password"
               v-model="password"
               :placeholder="$t('auth.signUpForm.password')"
               class="form-control"
               :class="{'form-control--error': v$.password.$error}"
               @focus="passwordActive = true"
               @blur="passwordActive = false"
               maxlength="60"
               autocomplete="one-time-code"
        />
        <span v-for="(error, index) of v$.password.$errors" :key="error.$uid"
              class="form-control-error"
        >
           <template v-if="index === 0 && !passwordActive">
            {{ error.$message }}
          </template>
        </span>
        <span v-if="passwordActive" class="form-control__char-count">
          {{60 - password.length}}
        </span>
      </div>
      <div class="field-wrap">
        <Select v-model="gender"
                :options="genderSelectOptions"
                optionLabel="name"
                overlayClass="sign-up-gender-select-overlay"
                class="sign-up-gender-select"
        />
      </div>
      <div class="field-wrap">
        <DatePicker v-model="birthday"
                    dateFormat="dd/mm/yy"
                    :class="{'p-datepicker--error': (v$.birthday.$error || !userAgeCheck)}"
                    :placeholder="$t('auth.signUpForm.selectDate')"
        />
        <span v-for="error of v$.birthday.$errors" :key="error.$uid"
              class="form-control-error"
        >
          {{ error.$message }}
        </span>
        <span v-if="!userAgeCheck && birthday" class="form-control-error">
          {{$t('validations.ageNotValid')}}
        </span>
      </div>
      <div class="form-info">
        <span class="form-label">
          {{$t('auth.signUpForm.byClickingJoin')}}
          <span class="form-label form-label--link" @click="openUserTerms()">
            {{$t('auth.signUpForm.terms')}}
          </span>.
        </span>
      </div>
      <div class="field-wrap">
        <button type="button"
                class="submit-btn"
                :class="{'disabled': !submitBtnState}"
                @click="signUp()"
        >{{$t('auth.join')}}</button>
      </div>
    </form>
    <div class="sign-up-view__bg-filter"></div>
  </div>
</app-base-view>
</template>

<style lang="scss">
.sign-up-view {
  height: 100vh;
  padding: 0 18px 18px;
  background: url("~@/assets/mobile/become-member-bg-image@2x.jpg"), no-repeat, center;
  background-size: cover;

  .wizard-view-header {
    margin-bottom: 50px;
  }

  &__bg-filter {
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    background-image: linear-gradient(180deg, rgba(0, 0, 0, .75), rgba(0, 0, 0, .18));
  }

  &__title {
    margin-bottom: 25px;
    font-size: 18px;
    font-weight: 600;
    color: #fff;
  }

  .sign-up-form {
    position: relative;
    z-index: 1;
  }

  .field-wrap {
    display: flex;
    flex-direction: column;
    position: relative;
    margin-bottom: 30px;

    .form-label {
      margin-bottom: 5px;
      color: #fff;
      font-weight: 600;
    }

    .form-control {
      padding: 12px;
      border-radius: 4px;
      background: #fff;
      border: solid 1px #d1d1d1;
      color: #4f4f4f;

      &::placeholder {
        color: #bbbbbb;
      }
      &:focus {
        border-color: #999999;
        outline-color: #a163cd;
        color: #000000;
      }
      &:focus-visible {
        outline-color: #a163cd;
      }
      &__char-count {
        position: absolute;
        right: 15px;
        bottom: 5px;
        display: flex;
        align-items: center;
        height: 35px;
        padding-left: 10px;
        border-left: 1px solid #ccc;
        line-height: 20px;
        font-size: 14px;
        color: #4f4f4f;
      }
      &__desc {
        position: absolute;
        left: 220px;
        font-size: 12px;
        color: #4f4f4f;
      }
      &--error {
        border-color: #fe8f18;
      }
    }

    .form-control-error {
      position: absolute;
      bottom: -20px;
      display: block;
      margin-top: 10px;
      font-size: 12px;
      color: #fe8f18;
    }

    .p-select {
      display: flex;
      align-items: center;
      height: 45px;
      width: 100%;
      border: 1px solid #cccccc;
      font-size: 12px;
      color: #4f4f4f;
      border-radius: 4px;
      background: #fff;

      &.p-focus {
        outline: none;
        border-color: #999999;
      }
      .p-select-label {
        font-size: 14px;
        color: #4f4f4f;
      }
    }

    .p-datepicker {
      height: 45px;
      width: 100%;
      min-width: 208px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      color: #4f4f4f;
      background: #fff;

      &--error {
        .p-datepicker-input {
          border-color: #fe8f18 !important;
        }
      }

      &.p-focus {
        outline: none;
        .p-inputtext {
          border-color: #999999;
          background: #fff;
        }
      }
      .p-inputtext {
        width: 100%;
        font-size: 14px;
        outline: none;
        border-color: #cccccc;
        background: #fff;
        color: #4f4f4f !important;

        &::placeholder {
          color: #bbbbbb;
        }
      }
    }

    .submit-btn {
      height: 40px;
      border-radius: 6px;
      background: #a163cd;
      font-size: 14px;
      font-weight: 600;
      border: none;
      color: #fff;

      &.disabled {
        background: #909090;
        cursor: default;
      }
    }

  }

  .form-info {
    margin-bottom: 20px;
    .form-label {
      font-size: 12px;
      font-weight: 600;
      color: #fff;
      &--link {
        color: #a163cd;
      }
    }

  }
}

.p-datepicker-panel {
  background: #fff !important;
  color: #4f4f4f !important;

  .p-datepicker-header {
    background: #fff !important;
  }

  .p-datepicker-select-month,
  .p-datepicker-select-year,
  .p-datepicker-day,
  .p-datepicker-year,
  .p-datepicker-month {
    color: #4f4f4f !important;
  }

  .p-datepicker-month-selected {
    background: #a163cc !important;
  }

  .p-datepicker-today > .p-datepicker-day {
    background: #e2e8f0 !important;
  }
}

.p-select-overlay {
  .p-select-option {
    background: #fff;
  }
}

</style>